import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Building2 } from 'lucide-react';
import { clubsService } from '../../../services';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';

const defaultForm = {
    name: '',
    address: '',
    description: '',
    introduction: '',
    hasCafe: false,
    hasShop: false,
    hasToilets: false,
    hasCarpark: false,
    venueType: '',
    offersClayShooting: false,
    offersRifleShooting: false,
    offersGameShooting: false,
    pricePerClay: '',
    clayPriceCurrency: 'GBP',
    wadPolicy: '',
    maxShotWeightGrams: '',
    cartridgeRestrictions: '',
    maxRifleDistance: '',
    rifleDistanceUnit: 'metres',
    hasElectronicTargets: false,
    hasCoveredFiringPoints: false,
    rifleCalibreRestrictions: '',
    gameTypesOffered: '',
    speciesAvailable: '',
    requiresNonToxicShot: false,
    dogsAllowed: false,
    bagLimitNotes: '',
    website: '',
    facebookUrl: '',
    instagramUrl: '',
    xUrl: '',
    youTubeUrl: '',
    tikTokUrl: '',
    logoUrl: '',
    primaryBrandColor: '#0f766e',
    secondaryBrandColor: '#f59e0b',
    openingHours: '',
    phoneNumber: '',
    email: ''
};

const EditClub = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(defaultForm);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await clubsService.getClubProfile(clubId);
            setForm({
                ...defaultForm,
                ...data,
                pricePerClay: data.pricePerClay ?? '',
                maxShotWeightGrams: data.maxShotWeightGrams ?? '',
                maxRifleDistance: data.maxRifleDistance ?? ''
            });
        } catch (err) {
            setError(err.message || 'Failed to load venue profile.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((current) => ({
            ...current,
            [name]: type === 'checkbox' ? checked : value
        }));
        setSuccess(null);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await clubsService.updateClubProfile(clubId, {
                ...form,
                pricePerClay: form.pricePerClay === '' ? null : Number(form.pricePerClay),
                maxShotWeightGrams: form.maxShotWeightGrams === '' ? null : Number(form.maxShotWeightGrams),
                maxRifleDistance: form.maxRifleDistance === '' ? null : Number(form.maxRifleDistance)
            });
            setSuccess('Venue profile updated.');
        } catch (err) {
            setError(err.message || 'Failed to save venue profile.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading venue profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            <button className="club-dashboard-back" onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}>
                <ChevronLeft size={18} />
                Back to Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-blue">
                        <Building2 size={24} />
                    </div>
                    <div>
                        <h1>Edit Venue Profile</h1>
                        <p>Manage the public venue profile content, branding, and metadata people see across the platform.</p>
                    </div>
                </div>
            </div>

            {error && <div className="manage-clubs-error">{error}</div>}
            {success && <div className="manage-clubs-error" style={{ background: '#e8f8ef', color: '#12715B' }}>{success}</div>}

            <div className="dashboard-card-container">
                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>General</h2>
                    <div className="form-group">
                        <label>Venue name</label>
                        <input name="name" value={form.name} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>Address</label>
                        <input name="address" value={form.address} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <textarea name="description" value={form.description || ''} onChange={handleInputChange} rows={3} />
                    </div>
                    <div className="form-group">
                        <label>Introduction</label>
                        <textarea name="introduction" value={form.introduction || ''} onChange={handleInputChange} rows={2} />
                    </div>
                    <div className="form-group">
                        <label>Venue type</label>
                        <select name="venueType" value={form.venueType || ''} onChange={handleInputChange}>
                            <option value="">Select type</option>
                            <option value="ClayGround">Clay Ground</option>
                            <option value="RifleRange">Rifle Range</option>
                            <option value="Estate">Estate</option>
                            <option value="Syndicate">Syndicate</option>
                            <option value="Wildfowl">Wildfowl</option>
                            <option value="Mixed">Mixed</option>
                        </select>
                    </div>
                    <div className="venue-chip-row">
                        <label className="member-role-chip role-gray"><input type="checkbox" name="dogsAllowed" checked={form.dogsAllowed} onChange={handleInputChange} /> Dogs allowed</label>
                        <label className="member-role-chip role-gray"><input type="checkbox" name="hasCafe" checked={form.hasCafe} onChange={handleInputChange} /> Cafe</label>
                        <label className="member-role-chip role-gray"><input type="checkbox" name="hasShop" checked={form.hasShop} onChange={handleInputChange} /> Shop</label>
                        <label className="member-role-chip role-gray"><input type="checkbox" name="hasToilets" checked={form.hasToilets} onChange={handleInputChange} /> Toilets</label>
                        <label className="member-role-chip role-gray"><input type="checkbox" name="hasCarpark" checked={form.hasCarpark} onChange={handleInputChange} /> Car park</label>
                    </div>
                    <div className="form-group">
                        <label>Opening hours</label>
                        <textarea
                            name="openingHours"
                            value={form.openingHours || ''}
                            onChange={handleInputChange}
                            rows={4}
                            placeholder={`Mon-Fri 09:00-17:00\nSat 08:00-18:00\nSun Closed`}
                        />
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>Premium Branding</h2>
                    <p className="club-describer" style={{ marginBottom: '16px' }}>
                        This controls presentation. Premium venue profiles can show richer branding on public and dashboard venue pages.
                    </p>
                    <div className="form-group">
                        <label>Logo URL</label>
                        <input
                            name="logoUrl"
                            value={form.logoUrl || ''}
                            onChange={handleInputChange}
                            placeholder="https://res.cloudinary.com/.../venue-logo.png"
                        />
                    </div>
                    <div className="dashboard-two-column-grid">
                        <div className="form-group">
                            <label>Primary Brand Colour</label>
                            <input type="color" name="primaryBrandColor" value={form.primaryBrandColor || '#0f766e'} onChange={handleInputChange} />
                        </div>
                        <div className="form-group">
                            <label>Secondary Brand Colour</label>
                            <input type="color" name="secondaryBrandColor" value={form.secondaryBrandColor || '#f59e0b'} onChange={handleInputChange} />
                        </div>
                    </div>
                    <div
                        style={{
                            borderRadius: '18px',
                            padding: '18px',
                            color: '#fff',
                            background: `linear-gradient(135deg, ${form.primaryBrandColor || '#0f766e'}, ${form.secondaryBrandColor || '#f59e0b'})`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px'
                        }}
                    >
                        {form.logoUrl ? (
                            <img
                                src={form.logoUrl}
                                alt="Venue logo preview"
                                style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '16px', background: '#fff' }}
                            />
                        ) : null}
                        <div>
                            <strong>{form.name || 'Venue branding preview'}</strong>
                            <p style={{ margin: '6px 0 0' }}>Logo and colours appear on the public venue profile and logged-in venue view.</p>
                        </div>
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>Clay</h2>
                    <label className="member-role-chip role-gray"><input type="checkbox" name="offersClayShooting" checked={form.offersClayShooting} onChange={handleInputChange} /> Offers clay shooting</label>
                    <div className="form-group">
                        <label>Price per clay (GBP)</label>
                        <input type="number" step="0.01" name="pricePerClay" value={form.pricePerClay} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>Wad policy</label>
                        <select name="wadPolicy" value={form.wadPolicy || ''} onChange={handleInputChange}>
                            <option value="">Not specified</option>
                            <option value="FibreOnly">Fibre only</option>
                            <option value="PlasticOnly">Plastic only</option>
                            <option value="Both">Both</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Max shot weight (grams)</label>
                        <input type="number" step="0.1" name="maxShotWeightGrams" value={form.maxShotWeightGrams} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>Cartridge restrictions</label>
                        <input name="cartridgeRestrictions" value={form.cartridgeRestrictions || ''} onChange={handleInputChange} placeholder="e.g. 12g and 20g only" />
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>Rifle</h2>
                    <label className="member-role-chip role-gray"><input type="checkbox" name="offersRifleShooting" checked={form.offersRifleShooting} onChange={handleInputChange} /> Offers rifle shooting</label>
                    <div className="form-group">
                        <label>Max rifle distance</label>
                        <input type="number" name="maxRifleDistance" value={form.maxRifleDistance} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>Distance unit</label>
                        <select name="rifleDistanceUnit" value={form.rifleDistanceUnit || 'metres'} onChange={handleInputChange}>
                            <option value="metres">Metres</option>
                            <option value="yards">Yards</option>
                        </select>
                    </div>
                    <label className="member-role-chip role-gray"><input type="checkbox" name="hasElectronicTargets" checked={form.hasElectronicTargets} onChange={handleInputChange} /> Electronic targets</label>
                    <label className="member-role-chip role-gray"><input type="checkbox" name="hasCoveredFiringPoints" checked={form.hasCoveredFiringPoints} onChange={handleInputChange} /> Covered firing points</label>
                    <div className="form-group">
                        <label>Calibre restrictions</label>
                        <input name="rifleCalibreRestrictions" value={form.rifleCalibreRestrictions || ''} onChange={handleInputChange} placeholder="e.g. up to .308" />
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>Game</h2>
                    <label className="member-role-chip role-gray"><input type="checkbox" name="offersGameShooting" checked={form.offersGameShooting} onChange={handleInputChange} /> Offers game shooting</label>
                    <div className="form-group">
                        <label>Game types offered</label>
                        <input name="gameTypesOffered" value={form.gameTypesOffered || ''} onChange={handleInputChange} placeholder="e.g. Driven, Walked-up" />
                    </div>
                    <div className="form-group">
                        <label>Species available</label>
                        <input name="speciesAvailable" value={form.speciesAvailable || ''} onChange={handleInputChange} placeholder="e.g. Pheasant, Partridge" />
                    </div>
                    <label className="member-role-chip role-gray"><input type="checkbox" name="requiresNonToxicShot" checked={form.requiresNonToxicShot} onChange={handleInputChange} /> Non-toxic shot required</label>
                    <div className="form-group">
                        <label>Bag limit notes</label>
                        <input name="bagLimitNotes" value={form.bagLimitNotes || ''} onChange={handleInputChange} />
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2 style={{ marginTop: 0 }}>Contact</h2>
                    <div className="form-group">
                        <label>Website</label>
                        <input name="website" value={form.website || ''} onChange={handleInputChange} />
                    </div>
                    <div className="dashboard-two-column-grid">
                        <div className="form-group">
                            <label>Facebook</label>
                            <input name="facebookUrl" value={form.facebookUrl || ''} onChange={handleInputChange} placeholder="https://facebook.com/yourvenue" />
                        </div>
                        <div className="form-group">
                            <label>Instagram</label>
                            <input name="instagramUrl" value={form.instagramUrl || ''} onChange={handleInputChange} placeholder="https://instagram.com/yourvenue" />
                        </div>
                        <div className="form-group">
                            <label>X / Twitter</label>
                            <input name="xUrl" value={form.xUrl || ''} onChange={handleInputChange} placeholder="https://x.com/yourvenue" />
                        </div>
                        <div className="form-group">
                            <label>YouTube</label>
                            <input name="youTubeUrl" value={form.youTubeUrl || ''} onChange={handleInputChange} placeholder="https://youtube.com/@yourvenue" />
                        </div>
                        <div className="form-group">
                            <label>TikTok</label>
                            <input name="tikTokUrl" value={form.tikTokUrl || ''} onChange={handleInputChange} placeholder="https://tiktok.com/@yourvenue" />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Phone</label>
                        <input name="phoneNumber" value={form.phoneNumber || ''} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label>Email</label>
                        <input name="email" value={form.email || ''} onChange={handleInputChange} />
                    </div>

                    <button className="manage-section-add-btn" onClick={handleSave} disabled={isSaving}>
                        <Save size={18} />
                        {isSaving ? 'Saving...' : 'Save Venue Profile'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditClub;
